import { useCallback, useEffect, useState } from "react";
import useDarkMode from '../../hooks/useDarkMode';
import { Link } from "react-router-dom";
import { SortOrderDropDownSelection } from "../../components/CustomComponents/DropdownSelections/DropDownSelections";
import Preloader from "../../components/CustomComponents/preLoad/preLoader";
import showErrorToast, { showSuccessToast } from "../../components/CustomComponents/toast/toast";
import useThemeColor from "../../hooks/useThemeColor";
import axiosInstance from "../../config/axiosApi/axiosApi";
import APIs from "../../Constants/apis";
import { useSelector, useDispatch } from "react-redux";
import Select from "react-select";
import DropDownStyles, { DropDownStylesDark } from "../staff/dropdownStyles";
import swal from "sweetalert";
import ColumnSelection from "../../components/CustomComponents/ColumnSelection/ColumnSelection";
import RenderCategoryRow from "./renderRowCategory";
import { setVisibleColumns, setSortOrder, setSortBy } from "../../app/itemGridPreferencesSlice";
import { mergeVisibleColumns, needsPreferencesUpdate } from "../../utils/preferencesUtils";
import { MANAGE_PAGE_SEARCH_DEBOUNCE_MS } from "../../utils/managePageConstants";
import ErrorBoundary from "../../components/CustomComponents/ErrorBoundary/ErrorBoundary.jsx";
import useIsChildBranch from "../../hooks/useIsChildBranch";
import "./../order/ManageOrderFilters.css";

const ManageCategories = () => {
  const CompID = useSelector((state) => state.auth.user).userData?.compID;
  const [search, setSearch] = useState("");
  const pageKey = "categories";
  const defaultVisibleColumns = {
    sortOrder: true,
    status: true,
    products: false,
    addOnCategory: false,
  };
  const defaultSortOrder = { value: "ASC", label: "ASC" };
  const defaultSortBy = { value: "rootCat_Orderby", label: "Order By" };
  const preferences = useSelector((state) => state.itemGridPreferences[pageKey] || {});
  const visibleColumns = mergeVisibleColumns(preferences.visibleColumns, defaultVisibleColumns);
  const sortOrder = preferences.sortOrder || defaultSortOrder;
  const sortBy = preferences.sortBy || defaultSortBy;
  const dispatch = useDispatch();

  // Get app settings from Redux
  const appSettings = useSelector((state) => state.appSettings);
  const { appTheme } = appSettings;

  // Check if dark mode is enabled
  const isDarkMode = useDarkMode();
  const dropdownStyles = isDarkMode ? DropDownStylesDark : DropDownStyles;

  // Get theme color from centralized hook
  const themeColor = useThemeColor();
  const isChildBranch = useIsChildBranch();



  // Error boundary for ResizeObserver
  useEffect(() => {
    const handleResizeObserverError = (e) => {
      if (
        e.message ===
        "ResizeObserver loop completed with undelivered notifications."
      ) {
        e.stopImmediatePropagation();
        e.preventDefault();
        return false;
      }
    };

    const handleUnhandledRejection = (e) => {
      if (
        e.reason &&
        e.reason.message &&
        e.reason.message.includes("ResizeObserver")
      ) {
        e.preventDefault();
        return false;
      }
    };

    // Add multiple event listeners to catch the error at different levels
    window.addEventListener("error", handleResizeObserverError, true);
    window.addEventListener("unhandledrejection", handleUnhandledRejection, true);

    // Also override console.error to suppress ResizeObserver errors
    const originalConsoleError = console.error;
    console.error = (...args) => {
      if (args[0] && typeof args[0] === 'string' && args[0].includes('ResizeObserver')) {
        return; // Suppress ResizeObserver console errors
      }
      originalConsoleError.apply(console, args);
    };

    return () => {
      window.removeEventListener("error", handleResizeObserverError, true);
      window.removeEventListener("unhandledrejection", handleUnhandledRejection, true);
      console.error = originalConsoleError;
    };
  }, []);

  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(false);

  const sortByOptions = [
    { value: "rootCat_Orderby", label: "Order By" },
    { value: "RootCat_ID", label: "ID" },
    { value: "RootCat_Name", label: "Name" },
  ];

  const clearFilters = () => {
    setSearch("");
  };

  const FetchCategories = useCallback(async () => {
    try {
      setLoading(true);
      const response = await axiosInstance.get(APIs.GET_CATEGORIES, {
        params: {
          CompID,
          SortBy: sortBy.value,
          SortOrder: sortOrder.value,
          Search: search?.trim() || "",
        },
      });

      const categoryData = response.data.data || response.data;

      if (Array.isArray(categoryData)) {
        setCategories(categoryData);
      } else {

        setCategories([]);
      }

      setLoading(false);
    } catch (err) {
      setLoading(false);

    }
  }, [search, sortBy, sortOrder, CompID]);

  // Function to find and delete a category within the hierarchy
  const findAndDeleteCategory = (categories, categoryId) => {
    return categories
      .map((category) => {
        if (category.rootCat_ID === categoryId) {
          return null; // Mark for deletion
        }
        if (category.subCategoriesList && category.subCategoriesList.length > 0) {
          return {
            ...category,
            subCategoriesList: findAndDeleteCategory(category.subCategoriesList, categoryId).filter(Boolean),
          };
        }
        return category;
      })
      .filter(Boolean); // Remove null entries
  };

  const DeleteCategory = async (category) => {
    const firstConfirm = await swal({
      title: "Are you sure?",
      text: `Are you sure you want to delete ${category.rootCat_Name}?`,
      icon: "warning",
      buttons: true,
      dangerMode: true,
    });

    if (!firstConfirm) return;

    const hasSubcategories = category.subCategoriesList && category.subCategoriesList.length > 0;

    if (hasSubcategories) {
      const secondConfirm = await swal({
        title: "Warning: Subcategories Detected",
        text: `Deleting ${category.rootCat_Name} will also delete its subcategories. Are you sure you want to proceed?`,
        icon: "warning",
        buttons: true,
        dangerMode: true,
      });

      if (!secondConfirm) return;
    }

    setLoading(true);
    try {
      const response = await axiosInstance.delete(APIs.DELETE_ROOT_CATEGORY, {
        params: {
          CompID,
          RootCat_ID: category.rootCat_ID,
        },
      });

      if (response.data.status) {
        setCategories((prevCategories) =>
          findAndDeleteCategory(prevCategories, category.rootCat_ID)
        );
        showSuccessToast(`${category.rootCat_Name} has been deleted.`);
      } else {
        showErrorToast(response.data.message || "Failed to delete category.");
      }

      setLoading(false);
    } catch (err) {
      showErrorToast("Something went wrong.");
      setLoading(false);
    }
  };

  useEffect(() => {
    // Check if preferences exist and merge with defaults to ensure new columns are included
    if (!preferences.visibleColumns) {
      dispatch(setVisibleColumns({ pageKey, columns: defaultVisibleColumns }));
    } else {
      // Check if preferences need updating (missing new columns or have removed columns)
      if (needsPreferencesUpdate(preferences.visibleColumns, defaultVisibleColumns)) {
        // Merge existing preferences with default columns (this handles both additions and removals)
        const mergedColumns = mergeVisibleColumns(preferences.visibleColumns, defaultVisibleColumns);
        dispatch(setVisibleColumns({ pageKey, columns: mergedColumns }));
      }
    }

    if (!preferences.sortOrder) {
      dispatch(setSortOrder({ pageKey, sortOrder: defaultSortOrder }));
    }
    if (!preferences.sortBy) {
      dispatch(setSortBy({ pageKey, sortBy: defaultSortBy }));
    }
    // eslint-disable-next-line
  }, [dispatch, pageKey]);

  useEffect(() => {
    const handler = setTimeout(() => {
      FetchCategories();
    }, MANAGE_PAGE_SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(handler);
  }, [FetchCategories]);

  return (
    <ErrorBoundary>
      <div>
        {loading && <Preloader />}
        <div className="d-flex align-items-center mb-3">
          <div>
            <ol className="breadcrumb">
              <li className="breadcrumb-item">
                <a href="#/">Home</a>
              </li>
              <li className="breadcrumb-item active">Categories</li>
            </ol>
            <h1 className="page-header mb-0">Categories</h1>
          </div>

          <div className="ms-auto">
            {!isChildBranch && (
              <Link to={"/categories/addCategory"}>
                <button
                  className="btn px-4"
                  style={{
                    backgroundColor: themeColor,
                    background: `linear-gradient(135deg, ${themeColor} 0%, ${themeColor}dd 100%)`,
                    border: 'none',
                    color: 'white',
                    borderRadius: '8px',
                    padding: '8px 16px',
                    fontSize: '14px',
                    fontWeight: '600',
                    transition: 'all 0.3s ease',
                    boxShadow: `0 2px 8px ${themeColor}40`
                  }}
                  onMouseEnter={(e) => {
                    e.target.style.background = `linear-gradient(135deg, ${themeColor}dd 0%, ${themeColor} 100%)`;
                    e.target.style.transform = 'translateY(-1px)';
                    e.target.style.boxShadow = `0 4px 12px ${themeColor}60`;
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.background = `linear-gradient(135deg, ${themeColor} 0%, ${themeColor}dd 100%)`;
                    e.target.style.transform = 'translateY(0)';
                    e.target.style.boxShadow = `0 2px 8px ${themeColor}40`;
                  }}
                >
                  <i className="fa fa-plus fa-lg me-2 ms-n2 text-white"></i>
                  Add Category
                </button>
              </Link>
            )}
          </div>
        </div>
        {/* <div className="mb-3 d-sm-flex fw-bold">
          <div className="mt-sm-0 mt-2">
            <a href="#/" className="text-dark text-decoration-none">
              <i className="fa fa-download fa-fw me-1 text-dark text-opacity-50"></i>{" "}
              Export
            </a>
          </div>
          <div className="ms-sm-4 ps-sm-1 mt-sm-0 mt-2">
            <a href="#/" className="text-dark text-decoration-none">
              <i className="fa fa-upload fa-fw me-1 text-dark text-opacity-50"></i>{" "}
              Import
            </a>
          </div>
        </div> */}
        <div className="card border-0">
          <div className="tab-content p-3">
            <div className="tab-pane fade show active" id="allTab">
              {/* Enhanced Filter Section */}
              <div className="mb-4">
                <div
                  className="card filter-card"
                  style={{
                    position: "relative",
                    zIndex: 1,
                    background: `linear-gradient(135deg, ${themeColor} 0%, ${themeColor}dd 100%)`,
                    borderRadius: '12px',
                    border: 'none',
                    boxShadow: `0 4px 20px ${themeColor}40`
                  }}
                >
                  <div className="card-body p-3">
                    {/* Filter Controls */}
                    <div className="row g-3 align-items-center">
                      {/* Search Field */}
                      <div className="col-xl-4 col-lg-4 col-md-6">
                        <div className="search-input filter-control">
                          <input
                            type="text"
                            className="filter-input"
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            placeholder="Search by name..."
                            style={{
                              backgroundColor: isDarkMode ? 'rgba(45, 55, 72, 0.9)' : 'rgba(255, 255, 255, 0.9)',
                              border: '1px solid rgba(255, 255, 255, 0.3)',
                              color: isDarkMode ? '#ffffff' : '#333'
                            }}
                          />
                        </div>
                      </div>

                      {/* Sort By Dropdown */}
                      <div className="col-xl-3 col-lg-3 col-md-6">
                        <div className="filter-control">
                          <Select
                            value={sortBy}
                            onChange={(val) => dispatch(setSortBy({ pageKey, sortBy: val }))}
                            options={sortByOptions}
                            styles={dropdownStyles}
                            placeholder="Sort By"
                            className="filter-dropdown"
                            menuPortalTarget={document.body}
                            menuPosition="fixed"
                            menuShouldBlockScroll={false}
                            menuShouldScrollIntoView={false}
                            closeMenuOnScroll={false}
                          />
                        </div>
                      </div>

                      {/* Sort Order Dropdown */}
                      <div className="col-xl-2 col-lg-2 col-md-6">
                        <div className="filter-control">
                          <SortOrderDropDownSelection
                            defaultValue={sortOrder}
                            onChange={(val) => dispatch(setSortOrder({ pageKey, sortOrder: val }))}
                          />
                        </div>
                      </div>

                      {/* Action Buttons */}
                      <div className="col-xl-3 col-lg-3 col-md-12">
                        <div className="d-flex gap-2">
                          <button
                            className="clear-btn"
                            onClick={clearFilters}
                            title="Clear all filters"
                          >
                            <i className="fa fa-times me-1"></i>
                            Filter
                          </button>

                          <ColumnSelection
                            visibleColumns={visibleColumns}
                            setVisibleColumns={(cols) =>
                              dispatch(setVisibleColumns({ pageKey, columns: cols }))
                            }
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="table-responsive" style={{ minHeight: "200px" }}>
                <table className="table table-hover">
                  <thead
                    className="bg-light"
                    style={{
                      position: "sticky",
                      top: 0,
                      zIndex: 10,
                      backgroundColor: isDarkMode ? "#2d3436" : "#f8f9fa",
                      color: isDarkMode ? "#ffffff" : "#000000",
                    }}
                  >
                    <tr>
                      <th className="pt-2 pb-2"></th>
                      <th className="pt-2 pb-2">Category</th>
                      {visibleColumns.sortOrder && (
                        <th className="pt-2 pb-2 text-center">Sort Order</th>
                      )}
                      {visibleColumns.status && (
                        <th className="pt-2 pb-2 text-center">Status</th>
                      )}
                      {visibleColumns.products && (
                        <th className="pt-2 pb-2 text-center">Products</th>
                      )}
                      {visibleColumns.addOnCategory && (
                        <th className="pt-2 pb-2 text-center">Add On Category</th>
                      )}
                      <th className="pt-2 pb-2 text-center">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {categories.map((category, index) => (
                      <RenderCategoryRow
                        key={category.rootCat_ID}
                        category={category}
                        index={index}
                        categories={categories}
                        setCategories={setCategories}
                        visibleColumns={visibleColumns}
                        DeleteCategory={DeleteCategory}
                        themeColor={themeColor}
                        isChildBranch={isChildBranch}
                      />
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>

        <style jsx>{`
        .table th,
        .table td {
          vertical-align: middle;
        }
        tr[draggable] {
          cursor: move;
        }
        .dropdown .no-caret::after {
          display: none;
        }
        .table tbody tr:hover {
          background-color: rgba(0, 0, 0, 0.02);
        }
      `}</style>
      </div>
    </ErrorBoundary>
  );
};

export default ManageCategories;